import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { TCustomerType } from "redux/reducers/models/facility";

import BookingFeeCustomerTypesTable from "./BookingFeeCustomerTypesTable";

type TBookingFeeCustomerTypesModalProps = {
  isOpen: boolean;
  selectedIds: number[];
  search: string;
  isLoading?: boolean;
  filteredTypes: TCustomerType[];
  onSearchChange: (search: string) => void;
  onApply: (selectedIds: number[]) => void;
  onClose: () => void;
  onCreateCustomerType?: () => void;
  onSort?: (key: keyof TCustomerType, direction: "asc" | "desc") => void;
};

export default function BookingFeeCustomerTypesModal(props: TBookingFeeCustomerTypesModalProps) {
  const { t } = useTranslation();
  const [bufferSelectedIds, setBufferSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (props.isOpen) {
      setBufferSelectedIds(props.selectedIds);
    }
  }, [props.isOpen, props.selectedIds]);

  const okDisabled = bufferSelectedIds.length === 0 && props.selectedIds.length === 0;

  function handleToggleType(id: number) {
    setBufferSelectedIds(prev => (prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]));
  }

  function handleSelectAllToggle(checked: boolean) {
    if (!checked) {
      const filteredIds = new Set(props.filteredTypes?.map(type => type?.id) ?? []);
      setBufferSelectedIds(prev => prev.filter(id => !filteredIds.has(id)));
      return;
    }

    setBufferSelectedIds(prev => {
      const next = new Set(prev);
      props.filteredTypes?.forEach(type => {
        if (type?.id != null) {
          next.add(type.id);
        }
      });
      return Array.from(next);
    });
  }

  return (
    <Portal isMounted={props.isOpen}>
      <Sheet
        open={props.isOpen}
        stacked
        size="small"
        title={t("Customer types")}
        supportText={t("Select the customer types this booking fee applies to.")}
        cancelText={t("Go back")}
        okText={t("Apply to booking fee")}
        okDisabled={okDisabled}
        onCancel={props.onClose}
        onOk={() => props.onApply(bufferSelectedIds)}
        secondaryFooter={
          <div className="booking-fees__customer-types-modal-footer-meta">
            <span>
              {bufferSelectedIds.length} {t("selected")}
            </span>
            {bufferSelectedIds.length > 0 ? (
              <button
                type="button"
                className="booking-fees__customer-types-modal-clear"
                onClick={() => setBufferSelectedIds([])}
              >
                {t("Clear all")}
              </button>
            ) : null}
          </div>
        }
      >
        <div className="booking-fees__customer-types-modal">
          <BookingFeeCustomerTypesTable
            search={props.search}
            onSearchChange={props.onSearchChange}
            filteredTypes={props.filteredTypes}
            selectedIds={bufferSelectedIds}
            isLoading={props.isLoading}
            onToggleType={handleToggleType}
            onSelectAllToggle={handleSelectAllToggle}
            onSort={props.onSort}
            onCreateCustomerType={props.onCreateCustomerType}
            searchInputId="booking-fee-customer-type-search"
            layout="modal"
          />
        </div>
      </Sheet>
    </Portal>
  );
}
